import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  Alert,
  AppBar,
  Toolbar,
  IconButton,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  List,
  ListItem
} from '@mui/material'
import {
  Restaurant,
  Logout,
  Assignment,
  CheckCircle,
  ExpandMore,
  ExpandLess
} from '@mui/icons-material'
import { useAuth } from '../../contexts/AuthContext'
import api from '../../utils/axios'

const AgroeconomiaDashboard = () => {
  const navigate = useNavigate()
  const { user, logout } = useAuth()
  const [editais, setEditais] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [selectedWinners, setSelectedWinners] = useState({})
  const [expandedEditais, setExpandedEditais] = useState({})

  useEffect(() => {
    fetchEditais()
  }, [])

  const fetchEditais = async () => {
    try {
      setLoading(true)
      const response = await api.get('/editais/list.php')
      if (response.data.success) {
        const editaisWithConcorrentes = await Promise.all(
          (response.data.editais || []).map(async (edital) => {
            try {
              const concorrentesResponse = await api.get(
                `/editais/concorrentes.php?edital_id=${edital.id}`
              )
              return {
                ...edital,
                concorrentes: concorrentesResponse.data.concorrentes || []
              }
            } catch (err) {
              return { ...edital, concorrentes: [] }
            }
          })
        )
        setEditais(editaisWithConcorrentes)
      } else {
        setError(response.data.message || 'Erro ao carregar editais')
      }
    } catch (err) {
      setError('Erro ao carregar editais. Tente novamente.')
    } finally {
      setLoading(false)
    }
  }

  const handleToggleEdital = (editalId) => {
    setExpandedEditais((prev) => ({
      ...prev,
      [editalId]: !prev[editalId]
    }))
  }

  const handleToggleWinner = (editalId, concorrenteId) => {
    setSelectedWinners((prev) => {
      const editalWinners = prev[editalId] || []
      const isSelected = editalWinners.includes(concorrenteId)
      return {
        ...prev,
        [editalId]: isSelected
          ? editalWinners.filter((id) => id !== concorrenteId)
          : [...editalWinners, concorrenteId]
      }
    })
  }

  const handleFinalizarEdital = async (editalId) => {
    const winners = selectedWinners[editalId] || []
    
    if (winners.length === 0) {
      setError('Selecione pelo menos um produtor ganhador')
      return
    }

    try {
      const response = await api.post('/editais/finalizar.php', {
        edital_id: editalId,
        produtores_selecionados: winners
      })

      if (response.data.success) {
        setSuccess('Edital finalizado com sucesso!')
        setSelectedWinners((prev) => {
          const newState = { ...prev }
          delete newState[editalId]
          return newState
        })
        fetchEditais()
        setTimeout(() => setSuccess(''), 3000)
      } else {
        setError(response.data.message || 'Erro ao finalizar edital')
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao finalizar edital')
    }
  }

  const isEditalFinalizado = (edital) => {
    return edital.status === 'finalizado'
  }

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar position="static">
        <Toolbar>
          <Restaurant sx={{ mr: 2 }} />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Agroeconomia - Editais
          </Typography>
          <Typography variant="body2" sx={{ mr: 2 }}>
            {user?.nome}
          </Typography>
          <IconButton color="inherit" onClick={handleLogout}>
            <Logout />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Typography variant="h5" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
          Gestão de Editais
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : editais.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Assignment sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              Nenhum edital cadastrado
            </Typography>
          </Paper>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {editais.map((edital) => {
              const expanded = expandedEditais[edital.id]
              const finalizado = isEditalFinalizado(edital)
              const winners = selectedWinners[edital.id] || []

              return (
                <Card key={edital.id} elevation={2}>
                  <CardContent>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 2
                      }}
                    >
                      <Box>
                        <Typography variant="h6">{edital.titulo}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {edital.descricao}
                        </Typography>
                      </Box>
                      <IconButton onClick={() => handleToggleEdital(edital.id)}>
                        {expanded ? <ExpandLess /> : <ExpandMore />}
                      </IconButton>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                      <Typography variant="body2">
                        <strong>Início:</strong>{' '}
                        {new Date(edital.data_inicio).toLocaleDateString('pt-BR')}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Fim:</strong>{' '}
                        {new Date(edital.data_fim).toLocaleDateString('pt-BR')}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Concorrentes:</strong> {edital.concorrentes?.length || 0}
                      </Typography>
                      {finalizado && (
                        <Typography variant="body2" color="success.main">
                          <strong>Finalizado</strong>
                        </Typography>
                      )}
                    </Box>

                    {expanded && (
                      <Box sx={{ mt: 3 }}>
                        {edital.concorrentes?.length > 0 ? (
                          <>
                            <Typography variant="subtitle1" gutterBottom>
                              Selecione os produtores ganhadores:
                            </Typography>
                            <List>
                              {edital.concorrentes.map((concorrente) => {
                                const isSelected = winners.includes(concorrente.id)
                                const isWinner = concorrente.selecionado === 1

                                return (
                                  <ListItem key={concorrente.id}>
                                    <FormControlLabel
                                      control={
                                        <Checkbox
                                          checked={isSelected}
                                          onChange={() =>
                                            handleToggleWinner(edital.id, concorrente.id)
                                          }
                                          disabled={finalizado}
                                        />
                                      }
                                      label={
                                        <Box>
                                          <Typography variant="body1">
                                            {concorrente.nome_produtor}
                                          </Typography>
                                          <Typography variant="body2" color="text.secondary">
                                            CAF: {concorrente.caf} | CPF: {concorrente.cpf}
                                          </Typography>
                                          {isWinner && (
                                            <Typography
                                              variant="body2"
                                              color="success.main"
                                              sx={{ fontWeight: 'bold' }}
                                            >
                                              ✓ Selecionado anteriormente
                                            </Typography>
                                          )}
                                        </Box>
                                      }
                                    />
                                  </ListItem>
                                )
                              })}
                            </List>

                            {!finalizado && (
                              <Button
                                variant="contained"
                                color="primary"
                                startIcon={<CheckCircle />}
                                onClick={() => handleFinalizarEdital(edital.id)}
                                disabled={winners.length === 0}
                                sx={{ mt: 2 }}
                              >
                                Finalizar Edital ({winners.length} selecionado
                                {winners.length !== 1 ? 's' : ''})
                              </Button>
                            )}
                          </>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Nenhum concorrente inscrito neste edital.
                          </Typography>
                        )}
                      </Box>
                    )}
                  </CardContent>
                </Card>
              )
            })}
          </Box>
        )}
      </Container>
    </Box>
  )
}

export default AgroeconomiaDashboard

